import IconButton from '@mui/material/IconButton';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Session from '../shared/Session';
import { titleCase } from '../shared/Utilities';
import Tooltip from '@mui/material/Tooltip';
import { copyTextToClipboard } from '../shared/Utilities';
import { DateTimeWidget, dateTimeText } from './DateTimeWidget';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CachedIcon from '@mui/icons-material/Cached';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import openapi_json from '../shared/openapi.json';

interface SimpleTableProps {
    path: string;
    data: any;
    dataComponent: any;
}

const SimpleTable: React.FC<SimpleTableProps> = ({ path, data, dataComponent }) => {
    let openapi: any = openapi_json;

    let plurals = ["data"];
    
    let schemaName = path.substring(1, path.length - 1);        //  "/ai/mini_projects/" -> "ai/mini_projects"
    let schemaMultiple = schemaName;                            //  "data"
    if (!plurals.includes(schemaName)) {
        console.log("NONPLURAL")
        schemaName = schemaName.substring(0, schemaName .length - 1);        //  "reports" -> "report"
        schemaMultiple = schemaName + "s";                      //  "reports"
    }
    console.log(path);
    console.log(schemaName);
    console.log(schemaMultiple);

    let schemaIdPath = path + `{${schemaName}_id}`;                   //  "/reports/{report_id}"

    let columns: any[] = [];
    if (data.length > 0) {
        columns = Object.keys(data[0]).map((column: any) => {
            return {
                field: column,
                headerName: titleCase(column),
                flex: 1,
                renderCell: (params: any) => {
                    if (params.value === null) {
                        return params.value;
                    }

                    let displayValue = params.value;
                    let tooltipValue = params.value.toString();;
                    let copyValue = params.value;

                    if(typeof params.value === 'object'){
                        displayValue = JSON.stringify(params.value, null, 4);
                        tooltipValue = displayValue.toString();
                        copyValue = JSON.stringify(params.value, null, 4);
                    }else if (column.startsWith("dt_")) {
                        try {
                            tooltipValue = dateTimeText(new Date(Date.parse(params.value)), false)
                            displayValue = <DateTimeWidget dtText={tooltipValue} />
                        } catch (error) {
                            console.log(error);
                        }
                    }

                    if (tooltipValue.length > 300) {
                        tooltipValue = tooltipValue.substring(0, 300) + "...";
                    }
                    
                    return <Tooltip key={params.id + "-" + column + "-tt"} title={tooltipValue} onClick={() => { copyTextToClipboard(copyValue); }}>
                        <span>{displayValue.toString()}</span>
                    </Tooltip>;
                }
            }
        });

        columns.push({
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params: any) => {
                let actionButtons = [];
                if (schemaIdPath in openapi.paths && "get" in openapi.paths[schemaIdPath]) {
                    actionButtons.push(
                        <Tooltip key={params.id + "-view"} title="View">
                            <IconButton key={params.id + "-view-button"} size="small" color="inherit" onClick={() => {
                                Session.navigate(`/${schemaMultiple}/${params.id}`);
                            }}>
                                <VisibilityIcon key={params.id + "-view-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }

                if (schemaIdPath in openapi.paths && "put" in openapi.paths[schemaIdPath]) {
                    actionButtons.push(
                        <Tooltip key={params.id + "-edit"} title="Edit">
                            <IconButton key={params.id + "-edit-button"} size="small" color="inherit" onClick={() => {
                                Session.navigate(`/${schemaMultiple}/edit/${params.id}`);
                            }}>
                                <EditIcon key={params.id + "-edit-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }
        
                if (schemaIdPath in openapi.paths && "delete" in openapi.paths[schemaIdPath]) {
                    actionButtons.push(
                        <Tooltip key={params.id + "-delete"} title="Delete">
                            <IconButton key={params.id + "-delete-button"} size="small" color="inherit" onClick={() => {
                                if(confirm(`Are you certain you want to delete ${schemaName}, ${params.id}? This delete may cascade into child resources.`)){
                                    Session.api("DELETE", `/${schemaMultiple}/${params.id}`, {}, () => {
                                        dataComponent.reloadData();
                                    });
                                }
                            }}>
                                <DeleteIcon key={params.id + "-delete-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }

                // Deployments
                if (schemaName === "deployment") {
                    actionButtons.push(
                        <Tooltip key={params.id + "-deploy"} title="Deploy">
                            <IconButton key={params.id + "-deploy-button"} size="small" color="inherit" onClick={() => {
                                Session.api("POST", "/tasks/", {
                                    name: "Deploy " + params.row.name,
                                    function: "deploy_deployment",
                                    parameters: {
                                        deployment_id: params.row.id
                                    }
                                });
                                Session.navigate("/tasks");
                            }}>
                                <RestartAltIcon key={params.id + "-deploy-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                    actionButtons.push(
                        <Tooltip key={params.id + "-reload"} title="Reload">
                            <IconButton key={params.id + "-reload-button"} size="small" color="inherit" onClick={() => {
                                Session.api("POST", "/tasks/", {
                                    name: "Update " + params.row.name,
                                    function: "reload_deployment",
                                    parameters: {
                                        deployment_id: params.row.id
                                    }
                                });
                                Session.navigate("/tasks");
                            }}>
                                <RotateLeftIcon key={params.id + "-reload-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }

                // Providers
                if (schemaName === "provider") {
                    actionButtons.push(
                        <Tooltip key={params.id + "-update"} title="Update">
                            <IconButton key={params.id + "-update-button"} size="small" color="inherit" onClick={() => {
                                Session.api("POST", "/tasks/", {
                                    name: "Update " + params.row.name,
                                    function: "deploy_provider",
                                    parameters: {
                                        provider_id: params.row.id
                                    }
                                });
                                Session.navigate("/tasks");
                            }}>
                                <RestartAltIcon key={params.id + "-update-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }

                // If restore is in path, then we're looking at backups
                if ("bucket" in data[0] && "filename" in data[0]) {
                    actionButtons.push(
                        <Tooltip key={params.id + "-restore"} title="Restore">
                            <IconButton key={params.id + "-restore-button"} size="small" color="inherit" onClick={() => {
                                window.location.href = Session.apiBaseurl + `/backups/restore?id=${params.id}`;
                            }}>
                                <RestoreIcon key={params.id + "-restore-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }
                
                // This is a hack to let us know we're looking at reports.
                if ("viewing_key" in data[0]) {
                    actionButtons.push(
                        <Tooltip key={params.id + "-view"} title="View">
                            <IconButton key={params.id + "-view-button"} size="small" color="inherit" onClick={() => {
                                window.location.href = Session.apiBaseurl + `/${schemaMultiple}/${params.id}/${params.row.viewing_key}`;
                            }}>
                                <VisibilityIcon key={params.id + "-view-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                    actionButtons.push(
                        <Tooltip key={params.id + "-rerun"} title="Rerun Report">
                            <IconButton key={params.id + "-rerun-button"} size="small" color="inherit" onClick={() => {
                                Session.api("POST", "/tasks/", {
                                    name: "Reload Report",
                                    function: "report",
                                    parameters: {
                                        report_id: params.row.report_id
                                    }
                                });
                                Session.navigate("/tasks");
                            }}>
                                <CachedIcon key={params.id + "-rerun-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                    actionButtons.push(
                        <Tooltip key={params.id + "-refresh"} title="Reload Report HTML">
                            <IconButton key={params.id + "-refresh-button"} size="small" color="inherit" onClick={() => {
                                Session.api("GET", `/${schemaMultiple}/${params.id}/refresh`);
                            }}>
                                <PlayArrowIcon key={params.id + "-refresh-icon"} />
                            </IconButton>
                        </Tooltip>
                    );
                }

                return actionButtons;
            }
        });
    }

    // if ("post" in openapi.paths[props.path]) {
    //     addButton = (
    //         <Button variant="contained" color="primary" style={{margin: "0.3%"}} onClick={() => Session.navigate(`/${schemaMultiple}/new`)}>Create New</Button>
    //     );
    // }

    // PAGINATION
    // https://stackoverflow.com/questions/66388174/material-ui-datagrid-pagination-is-starting-on-second-page-when-given-1-for-page
    // handlePageChange = (params) => {
    //     console.log("handlePageChange");
    //     console.log(params);
    //     console.log(params.page);
    //     console.log(params.pageSize);
    //     console.log(params.page * params.pageSize);
    //     console.log(params.page * params.pageSize + params.pageSize);
    //     console.log(props.data.slice(params.page * params.pageSize, params.page * params.pageSize + params.pageSize));
        // this.setState({
        //     page: params.page,
        //     pageSize: params.pageSize,
        //     rows: props.data.slice(params.page * params.pageSize, params.page * params.pageSize + params.pageSize)
        // });
    // };

    return <div style={{ height: '70vh', width: '100%' }}>
        {"post" in openapi.paths[path] &&
            <Button variant="contained" color="primary" style={{margin: "0.3%"}} onClick={() => Session.navigate(`/${schemaMultiple}/new`)}>Create New</Button>
        }
        {/* {addButton} */}
        <br/>
        <br/>
        <DataGrid
            disableSelectionOnClick={true}
            rows={data}
            columns={columns}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        id: false,
                        dt_created: false,
                        dt_modified: false
                    },
                },
            }}
        />
    </div>
}

export default SimpleTable;
